import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { HostListener } from '@angular/core';
import { Observable } from 'rxjs';

import { ComponentCanDeactivate } from 'src/app/common/pending-changes.guard';
import { QuestionService } from 'src/app/common/question-dialog/question-dialog.service';
import { Data2Service } from 'src/app/common/data2.service';
import { InfoboxService } from 'src/app/common/infobox/infobox.service';
import { LoaderService } from 'src/app/common/loader/loader.service';
import { TourismMapService, Vehicle, City, Transport, Action, Reservation } from './tourism-map.service';

@Component({
	selector: 'app-tourism-map',
	templateUrl: './tourism-map.component.html',
	styleUrls: ['./tourism-map.component.css'],
})
export class TourismMapComponent implements OnInit, ComponentCanDeactivate {
	saveStatus = 'unsaved';
	side = '';
	map: Array<any> = [];
	connections: Array<any> = [];
	vehicleSelected: Vehicle;
	citySelected: City;
	showVehicles = true;
	tooltip = {
		data: null,
		x: 0,
		y: 0,
	};
	vehicles: Array<Vehicle> = [];
	vehiclesAll: Array<Vehicle> = [];
	cities: Array<City> = [];
	destinations: Array<any> = [];
	transports: Array<Transport> = [];
	actions: Array<Action> = [];
	timetable = {
		id: null,
		created: null,
		side: null,
		sideName: null,
		destinations: null,
		seasons: [],
		transports: this.transports,
		actions: this.actions,
		vehicles: this.vehicles,
	};
	editable = false;
	private readonly lockTime = 15; // minutes
	private lockTimer;
	private lockTimerSeconds: number;
	lockTimerText = '';
	questionSubscription;

	constructor(
		public mapService: TourismMapService,
		private readonly api: Data2Service,
		private readonly loader: LoaderService,
		private readonly infobox: InfoboxService,
		private readonly question: QuestionService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
	) {
		mapService.transportsChange.subscribe((transports) => {
			this.transports = transports;
			this.timetable.transports = this.transports;
			this.generateConnections();
			this.saveStatus = 'unsaved';
		});
		mapService.actionsChange.subscribe((actions) => {
			this.actions = actions;
			this.timetable.actions = this.actions;
			this.saveStatus = 'unsaved';
		});
	}

	@HostListener('window:beforeunload') canDeactivate(): Observable<boolean> | boolean {
		return !this.editable || this.saveStatus == 'saved';
	}

	ngOnInit(): void {
		if (!this.route.snapshot.params.id) {
			this.infobox.error('Brak ID');
			return;
		}
		this.mapService.reset();
		this.timetable.id = this.route.snapshot.params.id;
		this.api.get('/vehicles/?status=2&types=bus,autokar', (result, error) => {
			this.vehiclesAll = result.data;
			this.api.get('/tourismTimetable/' + this.timetable.id + '/', (result, error) => {
				if (error) {
					return;
				}
				this.timetable.side = result.data.side;
				this.timetable.created = result.data.created;
				this.timetable.sideName = result.data.side == 'departure' ? 'Wyjazd' : 'Powrót';
				this.timetable.destinations = result.data.seasons;
				this.timetable.seasons = result.data.seasons.map((obj) => {
					return obj.season;
				});
				const transports = result.data.transports;
				const actions = result.data.actions;
				const vehicles = result.data.vehicles;
				this.api.get('/tourismStops/?ids=' + this.timetable.seasons, (result, error) => {
					result.data = result.data.map(function (item) {
						item.domestic = parseInt(item.domestic);
						return item;
					});
					this.cities = result.data;
					this.getPeople(() => {
						this.loadData(transports, actions, vehicles);
						this.generateMap();
					});
				});
				this.api.get('/season/?ids=' + this.timetable.seasons, (result, error) => {
					this.destinations = result.data;
				});
			});
		});
	}

	private loadData(transports, actions, vehicles) {
		vehicles.forEach((vehicle) => {
			const addVehicle = this.vehiclesAll.find((obj) => obj.id == vehicle.vehicle);
			addVehicle.occupied = 0;
			addVehicle.crew = 0;
			this.vehicles.push(addVehicle);
		});
		transports.sort((a, b) => {
			if (a.id < b.id) {
				return -1;
			}
			if (a.id > b.id) {
				return 1;
			}
			return 0;
		});
		transports.forEach((transport) => {
			transport.domestic = transport.domestic == '1' ? true : false;
			const vehicle = this.vehiclesAll.find((vehicle) => vehicle.id == transport.vehicle);
			const city = this.cities.find((city) => city.id == transport.city);
			const transportActions = actions.filter((obj) => obj.transport == transport.id);
			if (!transport.domestic) {
				const destination = this.destinations.find((obj) => obj.stopTo == transport.city);
				this.mapService.endTrip(destination, vehicle);
				return;
			}
			if (!transportActions.length) {
				this.mapService.driveThru(city, vehicle);
				return;
			}
			transportActions.forEach((action) => {
				switch (action.action) {
					case 'get':
						const reservation: Reservation = city.reservations.find(
							(obj) => obj.number == action.reservation,
						);
						if (!reservation) {
							this.infobox.error(
								'Rezerwacja ' +
									action.reservation +
									' nie została znaleziona ' +
									'(Miasto: ' +
									city.name +
									')',
							);
							return;
						}
						this.mapService.getPeople(reservation, city, vehicle);
						break;
					case 'put':
						const prevAction: Action = this.actions.find(
							(obj) => obj.action == 'get' && obj.reservation.number == action.reservation,
						);
						if (!prevAction) {
							this.infobox.error('Rezerwacja ' + action.reservation + ' nie została znaleziona');
							return;
						}
						this.mapService.putPeople(prevAction, city, vehicle);
						break;
				}
			});
		});
	}

	undo() {
		if (!this.editable) {
			return;
		}
		this.mapService.undo();
	}

	edit() {
		this.loader.show();
		this.api.get('/locks/tourismMap/' + this.timetable.id + '/' + this.lockTime + '/', (result, error) => {
			this.loader.hide();
			if (error) {
				return;
			}
			this.editable = true;
			this.lockTimerSeconds = this.lockTime * 60;
			this.lockTimerText = this.lockTime + ':00';
			if (this.lockTimer) {
				clearInterval(this.lockTimer);
			}
			this.lockTimer = setInterval(() => {
				this.lockTimerSeconds--;
				if (this.lockTimerSeconds < 0) {
					this.loader.show();
					window.location.reload();
					return;
				}
				const minutes = Math.floor(this.lockTimerSeconds / 60);
				const seconds = this.lockTimerSeconds - minutes * 60;
				const minutesText = minutes < 10 ? '0' + minutes.toString() : minutes.toString();
				const secondsText = seconds < 10 ? '0' + seconds.toString() : seconds.toString();
				this.lockTimerText = minutesText + ':' + secondsText;
			}, 1000);
		});
	}

	save() {
		if (this.cities.filter((obj) => obj.reservations && obj.reservations.length).length) {
			this.infobox.error('Nie można zapisać ponieważ są rezerwacje do wzięcia');
			return;
		}
		this.loader.show();
		this.saveStatus = 'saving';
		this.api.put('/tourismTimetable/' + this.timetable.id + '/', this.timetable, (result, error) => {
			this.loader.hide();
			if (error) {
				return;
			}
			this.api.del('/locks/tourismMap/' + this.timetable.id, (result, error) => {});
			clearInterval(this.lockTimer);
			this.editable = false;
			this.infobox.success('Zapisano');
			this.saveStatus = 'saved';
		});
	}

	endTrip(destination) {
		if (!this.editable) {
			return;
		}
		this.mapService.endTrip(destination, this.vehicleSelected);
		this.generateConnections();
		this.save();
	}

	showTooltip(city) {
		if (!city) {
			this.tooltip.x = 0;
			this.tooltip.y = 0;
			return;
		}
		if (city.people <= 0) {
			return;
		}
		this.tooltip.data = city;
		const cityPos = this.getOffset('city' + city.id);
		this.tooltip.x = cityPos.left;
		this.tooltip.y = cityPos.top + cityPos.height;
	}

	citySelect(city) {
		if (!this.vehicleSelected) {
			return;
		}
		this.citySelected = city;
	}

	mapPeopleClose() {
		this.citySelected = null;
		this.generateConnections();
	}

	vehicleSelect(data) {
		const other = this.vehicles.map((vehicle) => {
			vehicle.selected = false;
			return vehicle;
		});
		data.selected = true;
		this.vehicleSelected = data;
		this.showVehicles = false;
		this.generateConnections();
	}

	private generateMap() {
		this.generateCities();
		this.loader.hide();
	}

	private getPeople(callback) {
		this.api.command(
			'/tourismMap/',
			{
				cmd: 'getPeople',
				side: this.timetable.side,
				ids: this.timetable.seasons,
			},
			(result, error) => {
				this.cities.forEach((city) => {
					if (city.domestic && (!city.x || !city.y)) {
						this.infobox.error('Brak pozycji dla miasta: ' + city.name);
						return;
					}
					const data = result.data.filter((obj) => obj.from.id == city.id);
					city.reservations = [];
					city.people = 0;
					city.assigned = 0;
					city.options = 0;
					data.forEach((pos) => {
						let reservation: Reservation = city.reservations.find((res) => res.number == pos.reservation);
						if (!reservation) {
							reservation = {
								number: pos.reservation,
								people: 0,
								option: false,
								destinationName: pos.destinationName,
								originCity: city,
								amount: pos.amount,
								warning: pos.warning ? pos.warning : this.isReservationExistsInOtherCity(pos, city),
							};
							city.reservations.push(reservation);
						}
						reservation.people += 1;
						if (pos.status == '3') {
							reservation.option = true;
							city.options += 1;
						}
						city.people += 1;
					});
					if (city.reservations.length) {
						city.primary = true;
					}
				});
				this.informOther(result.data);
				callback();
			},
		);
	}

	private isReservationExistsInOtherCity(thisReservation, thisCity): string | null {
		let warning: string | null = null;
		this.cities.forEach((city) => {
			if (city.id == thisCity.id || !city.reservations || !city.reservations.length) {
				return;
			}

			const reservation: Reservation = city.reservations.find((res) => res.number == thisReservation.reservation);
			if (!reservation) {
				return;
			}
			warning = 'Rezerwacja jest też w mieście: ' + city.name;
			reservation.warning = 'Rezerwacja jest też w mieście: ' + thisCity.name;
		});
		return warning;
	}

	private generateCities() {
		this.map = [];
		for (let rowNum = 1; rowNum <= 16; rowNum++) {
			const columns = [];
			for (let colNum = 1; colNum <= 9; colNum++) {
				const city = this.cityFindByPos(colNum, rowNum);
				columns.push(city);
			}
			this.map.push(columns);
		}
	}

	private cityFindByPos(x, y) {
		const city = this.cities.find((objFind) => objFind.x == x && objFind.y == y);
		if (city) {
			// city.assigned = 0;
			return city;
		}
		return {};
	}

	generateConnectionsAll() {
		this.connections = [];
		for (let i = this.transports.length - 1; i >= 0; i--) {
			if (i <= 0) {
				return;
			}
			this.connect('city' + this.transports[i].city.id, 'city' + this.transports[i - 1].city.id);
		}
	}

	private generateConnections() {
		if (!this.vehicleSelected) {
			return;
		}
		const transports = this.transports.filter((obj) => {
			return obj.vehicle.id == this.vehicleSelected.id;
		});
		this.connections = [];
		for (let i = transports.length - 1; i >= 0; i--) {
			if (i <= 0) {
				return;
			}
			this.connect('city' + transports[i].city.id, 'city' + transports[i - 1].city.id);
		}
	}

	private connect(div1, div2) {
		const off1 = this.getOffset(div1);
		const off2 = this.getOffset(div2);
		let x1 = 0;
		let x2 = 0;
		let y1 = 0;
		let y2 = 0;
		if (off1.left == off2.left) {
			x1 = off1.left + off1.width / 2;
			x2 = off2.left + off2.width / 2;
		} else if (off1.left > off2.left) {
			x1 = off1.left;
			x2 = off2.left + off2.width;
		} else {
			x1 = off1.left + off1.width;
			x2 = off2.left;
		}
		if (off1.top == off2.top) {
			y1 = off1.top + off1.height / 2;
			y2 = off2.top + off2.height / 2;
		} else if (off1.top > off2.top) {
			y1 = off1.top;
			y2 = off2.top + off2.height;
		} else {
			y1 = off1.top + off1.height;
			y2 = off2.top;
		}
		const length = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
		const cx = (x1 + x2) / 2 - length / 2;
		const cy = (y1 + y2) / 2 - 2;
		const angle = Math.atan2(y1 - y2, x1 - x2) * (180 / Math.PI);
		this.connections.push({
			x: cx,
			y: cy,
			length: length,
			angle: angle,
		});
	}

	private getOffset(elementId) {
		const element = document.getElementById(elementId);
		const rect = element.getBoundingClientRect();
		return {
			left: rect.left + window.pageXOffset,
			top: rect.top + window.pageYOffset,
			width: rect.width || element.offsetWidth,
			height: rect.height || element.offsetHeight,
		};
	}

	resetAsk() {
		this.question.ask('Czy na pewno zresetować?', 'deleteAsk');
		this.questionSubscription = this.question.status.subscribe((result) => {
			if (result.dialogId != 'deleteAsk') {
				return;
			}
			if (!result.result) {
				return;
			}
			this.loader.show();
			this.api.command('/tourismTimetable/', { cmd: 'reset', id: this.timetable.id }, (result, error) => {
				this.loader.hide();
				if (error) {
					return;
				}
				location.reload();
			});
		});
	}
	private informOther(data) {
		data.filter((obj) => obj.other == '1').forEach((reservation) => {
			this.infobox.error(
				'Rezerwacja ' +
					reservation.reservation +
					'  ma inne miasta wyjazd:' +
					reservation.from?.name +
					' powrót:' +
					reservation.to?.name,
			);
		});
	}
}
